import React, { useState, useEffect } from 'react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { useFamilyContext } from '../../../../contexts/FamilyContext';
import { supabase } from '../../../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, AlertCircle, CheckCircle, ChevronRight,
  Plus, Edit3, Trash2, X, Target, TrendingUp, DollarSign,
  FileText, School, Flag, Star, Info, Bell
} from 'lucide-react';

const TimelineSection = () => {
  const { selectedStudent, studentProgress } = useParentPortal();
  const { currentUser, family } = useFamilyContext();
  const navigate = useNavigate();

  const [deadlines, setDeadlines] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingDeadline, setAddingDeadline] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'deadline', 'milestone', 'financial'

  const [newDeadline, setNewDeadline] = useState({
    title: '',
    date: '',
    type: 'deadline', // 'deadline', 'milestone', 'financial'
    category: 'application', // 'application', 'test', 'financial', 'other'
    description: '',
    college: '',
    reminder_days: 7
  });

  // Pre-defined important dates
  const standardDeadlines = [
    {
      title: 'FAFSA Opens',
      date: '2024-10-01',
      type: 'financial',
      category: 'financial',
      description: 'Federal Student Aid application opens for the academic year',
      isStandard: true
    },
    {
      title: 'CSS Profile Opens',
      date: '2024-10-01',
      type: 'financial',
      category: 'financial',
      description: 'College Scholarship Service Profile for institutional aid',
      isStandard: true
    },
    {
      title: 'Common App Opens',
      date: '2024-08-01',
      type: 'milestone',
      category: 'application',
      description: 'Common Application platform opens for new applicants',
      isStandard: true
    },
    {
      title: 'Early Decision I Deadline',
      date: '2024-11-01',
      type: 'deadline',
      category: 'application',
      description: 'Typical ED1 deadline for most colleges',
      isStandard: true
    },
    {
      title: 'Early Action Deadline',
      date: '2024-11-15',
      type: 'deadline',
      category: 'application',
      description: 'Typical EA deadline for most colleges',
      isStandard: true
    },
    {
      title: 'UC Application Deadline',
      date: '2024-11-30',
      type: 'deadline',
      category: 'application',
      description: 'University of California system application deadline',
      isStandard: true
    },
    {
      title: 'Regular Decision Deadline',
      date: '2025-01-01',
      type: 'deadline',
      category: 'application',
      description: 'Typical RD deadline for most colleges',
      isStandard: true
    },
    {
      title: 'FAFSA Priority Deadline',
      date: '2025-02-01',
      type: 'financial',
      category: 'financial',
      description: 'Priority deadline for federal financial aid',
      isStandard: true
    }
  ];

  useEffect(() => {
    if (selectedStudent) {
      loadTimeline();
    }
  }, [selectedStudent]);

  const loadTimeline = async () => {
    if (!selectedStudent) return;

    setLoading(true);
    try {
      const studentId = selectedStudent.auth_user_id || selectedStudent.id;

      // Load custom deadlines from database
      const { data: customData, error } = await supabase
        .from('college_deadlines')
        .select('*')
        .eq('student_id', studentId)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error loading deadlines:', error);
      } else {
        // Combine standard and custom deadlines
        const allDeadlines = [
          ...standardDeadlines.map(d => ({ ...d, id: `standard-${d.title}` })),
          ...(customData || [])
        ];
        setDeadlines(allDeadlines);
      }

      // Load milestones
      const { data: milestoneData } = await supabase
        .from('college_milestones')
        .select('*')
        .eq('student_id', studentId)
        .order('date', { ascending: true });

      if (milestoneData) {
        setMilestones(milestoneData);
      }
    } catch (error) {
      console.error('Error loading timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeadline = async () => {
    if (!newDeadline.title || !newDeadline.date || !selectedStudent) return;

    try {
      const studentId = selectedStudent.auth_user_id || selectedStudent.id;

      const { error } = await supabase
        .from('college_deadlines')
        .insert({
          title: newDeadline.title,
          date: newDeadline.date,
          type: newDeadline.type,
          category: newDeadline.category,
          description: newDeadline.description,
          college: newDeadline.college,
          reminder_days: newDeadline.reminder_days,
          student_id: studentId,
          created_by: currentUser.id
        });

      if (error) throw error;

      setNewDeadline({
        title: '',
        date: '',
        type: 'deadline',
        category: 'application',
        description: '',
        college: '',
        reminder_days: 7
      });
      setAddingDeadline(false);
      loadTimeline();
    } catch (error) {
      console.error('Error adding deadline:', error);
      alert('Failed to add deadline. Please try again.');
    }
  };

  const handleDeleteDeadline = async (deadlineId) => {
    if (!confirm('Are you sure you want to remove this deadline?')) return;

    try {
      const { error } = await supabase
        .from('college_deadlines')
        .delete()
        .eq('id', deadlineId);

      if (error) throw error;
      loadTimeline();
    } catch (error) {
      console.error('Error deleting deadline:', error);
      alert('Failed to remove deadline. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (days) => {
    if (days < 0) return 'text-red-600 bg-red-50';
    if (days <= 7) return 'text-red-600 bg-red-50';
    if (days <= 30) return 'text-amber-600 bg-amber-50';
    if (days <= 60) return 'text-blue-600 bg-blue-50';
    return 'text-slate-600 bg-slate-50';
  };

  const getStatusText = (days) => {
    if (days < 0) return `${Math.abs(days)} days ago`;
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days <= 7) return `${days} days`;
    if (days <= 30) return `${Math.round(days / 7)} weeks`;
    return `${Math.round(days / 30)} months`;
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'deadline': return <Target className="w-4 h-4" />;
      case 'milestone': return <Flag className="w-4 h-4" />;
      case 'financial': return <DollarSign className="w-4 h-4" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'deadline': return 'text-coral-600 bg-coral-100';
      case 'milestone': return 'text-azure-600 bg-azure-100';
      case 'financial': return 'text-emerald-600 bg-emerald-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  // Filter deadlines based on selected filter
  const filteredDeadlines = deadlines.filter(deadline => {
    if (filterType === 'all') return true;
    return deadline.type === filterType;
  });

  // Calculate progress statistics
  const upcomingDeadlines = deadlines.filter(d => getDaysUntil(d.date) >= 0 && getDaysUntil(d.date) <= 30);
  const overdueDeadlines = deadlines.filter(d => getDaysUntil(d.date) < 0);
  const completedMilestones = milestones.filter(m => m.completed).length;
  const totalMilestones = milestones.length;

  if (!selectedStudent) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          No Student Selected
        </h2>
        <p className="text-slate-600">
          Select a student to view their college application timeline.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Timeline & Deadlines</h1>
          <p className="text-slate-600 mt-1">
            Track important dates for {selectedStudent.name}'s college applications
          </p>
        </div>
        <button
          onClick={() => navigate('/college/timeline')}
          className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2"
        >
          View Full Timeline
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{upcomingDeadlines.length}</div>
              <div className="text-sm text-slate-600">Next 30 Days</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{overdueDeadlines.length}</div>
              <div className="text-sm text-slate-600">Overdue</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">
                {completedMilestones}/{totalMilestones}
              </div>
              <div className="text-sm text-slate-600">Milestones</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{deadlines.length}</div>
              <div className="text-sm text-slate-600">Total Events</div>
            </div>
          </div>
        </div>
      </div>

      {/* Urgent Deadlines Alert */}
      {upcomingDeadlines.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900">Urgent Deadlines</h3>
              <p className="text-red-700 text-sm mt-1">
                {upcomingDeadlines.length} deadline{upcomingDeadlines.length > 1 ? 's' : ''} in the next 30 days
              </p>
              <div className="mt-3 space-y-2">
                {upcomingDeadlines.slice(0, 3).map((deadline) => (
                  <div key={deadline.id} className="flex items-center justify-between text-sm">
                    <span className="text-red-800 font-medium">{deadline.title}</span>
                    <span className="text-red-600">{formatDate(deadline.date)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {['all', 'deadline', 'milestone', 'financial'].map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  filterType === type
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
                {type === 'all' && ` (${deadlines.length})`}
              </button>
            ))}
          </div>
          <button
            onClick={() => setAddingDeadline(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
          >
            <Plus size={16} />
            Add Deadline
          </button>
        </div>
      </div>

      {/* Add Deadline Form */}
      {addingDeadline && (
        <div className="bg-white rounded-xl border-2 border-teal-500 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Add New Deadline</h3>
            <button
              onClick={() => setAddingDeadline(false)}
              className="text-slate-500 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={newDeadline.title}
                onChange={(e) => setNewDeadline({...newDeadline, title: e.target.value})}
                placeholder="e.g., Stanford Early Action"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Date *
              </label>
              <input
                type="date"
                value={newDeadline.date}
                onChange={(e) => setNewDeadline({...newDeadline, date: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Type
              </label>
              <select
                value={newDeadline.type}
                onChange={(e) => setNewDeadline({...newDeadline, type: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              >
                <option value="deadline">Deadline</option>
                <option value="milestone">Milestone</option>
                <option value="financial">Financial</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Category
              </label>
              <select
                value={newDeadline.category}
                onChange={(e) => setNewDeadline({...newDeadline, category: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              >
                <option value="application">Application</option>
                <option value="test">Test/Exam</option>
                <option value="financial">Financial Aid</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Reminder (days before)
              </label>
              <input
                type="number"
                value={newDeadline.reminder_days}
                onChange={(e) => setNewDeadline({...newDeadline, reminder_days: parseInt(e.target.value) || 7})}
                min="1"
                max="90"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                College (Optional)
              </label>
              <input
                type="text"
                value={newDeadline.college}
                onChange={(e) => setNewDeadline({...newDeadline, college: e.target.value})}
                placeholder="e.g., Stanford University"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={newDeadline.description}
                onChange={(e) => setNewDeadline({...newDeadline, description: e.target.value})}
                placeholder="Additional details..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleAddDeadline}
              disabled={!newDeadline.title || !newDeadline.date}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              Add Deadline
            </button>
            <button
              onClick={() => setAddingDeadline(false)}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Timeline List */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Upcoming Deadlines & Milestones</h3>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <Clock className="w-8 h-8 text-slate-400 mx-auto mb-2 animate-spin" />
              <p className="text-slate-600">Loading timeline...</p>
            </div>
          ) : filteredDeadlines.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-slate-900 mb-2">No deadlines yet</h4>
              <p className="text-slate-600">Add important dates and milestones to track</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDeadlines.map((deadline) => {
                const daysUntil = getDaysUntil(deadline.date);
                const statusColor = getStatusColor(daysUntil);
                const statusText = getStatusText(daysUntil);

                return (
                  <div
                    key={deadline.id}
                    className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(deadline.type)}`}>
                      {getTypeIcon(deadline.type)}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900">
                            {deadline.title}
                            {deadline.college && (
                              <span className="text-slate-500 font-normal ml-2">• {deadline.college}</span>
                            )}
                          </h4>
                          {deadline.description && (
                            <p className="text-sm text-slate-600 mt-1">{deadline.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-slate-600">
                              {formatDate(deadline.date)}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                              {statusText}
                            </span>
                            {deadline.isStandard && (
                              <span className="text-xs text-slate-500 italic">Standard deadline</span>
                            )}
                          </div>
                        </div>

                        {!deadline.isStandard && (
                          <button
                            onClick={() => handleDeleteDeadline(deadline.id)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove deadline"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Application Progress */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-azure-600" />
            Application Progress
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Colleges Added</span>
              <span className="font-medium text-slate-900">{studentProgress?.colleges?.length || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Essays Started</span>
              <span className="font-medium text-slate-900">{studentProgress?.essaysStarted || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Applications Submitted</span>
              <span className="font-medium text-slate-900">{studentProgress?.applicationsSubmitted || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Recommendations Requested</span>
              <span className="font-medium text-slate-900">{studentProgress?.recommendationsRequested || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Financial Overview
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">FAFSA Status</span>
              <span className={`font-medium ${studentProgress?.fafsaComplete ? 'text-emerald-600' : 'text-amber-600'}`}>
                {studentProgress?.fafsaComplete ? 'Complete' : 'Not Started'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">CSS Profile</span>
              <span className={`font-medium ${studentProgress?.cssProfileComplete ? 'text-emerald-600' : 'text-amber-600'}`}>
                {studentProgress?.cssProfileComplete ? 'Complete' : 'Not Started'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Est. Application Fees</span>
              <span className="font-medium text-slate-900">
                ${(studentProgress?.colleges?.length || 0) * 75}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Fee Waivers</span>
              <span className="font-medium text-slate-900">{studentProgress?.feeWaivers || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pro Tip */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl border border-indigo-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-indigo-900 mb-2">Timeline Best Practices</h3>
            <ul className="text-sm text-indigo-800 space-y-1">
              <li>• Submit applications at least 1 week before deadlines to avoid technical issues</li>
              <li>• Request recommendations 4-6 weeks before they're needed</li>
              <li>• Complete FAFSA as soon as it opens for maximum aid eligibility</li>
              <li>• Set calendar reminders for all deadlines with buffer time</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineSection;